import {
  ArrowsClockwise,
  DotsThree,
  DownloadSimple,
  GitBranch,
  Plus,
  Stethoscope,
  Trash,
} from "@phosphor-icons/react";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { GitConnection } from "~/api/gitTypes";
import {
  useGitConnections,
  useIngestGitConnection,
  useProjects,
  useRemoveGitConnection,
  useRotateGitConnection,
  useTestGitConnection,
  useUpdateGitConnection,
} from "~/api/queries";
import { GitConnectionDialog } from "~/features/git/GitConnectionDialog";
import { kindLabel, repositoryName, STATUS_TONE, statusLabel } from "~/features/git/gitLabels";
import { Pill, Row, Rows } from "~/features/git/parts";
import {
  type ConnectPrefill,
  parseConnectPrefill,
  parseRemote,
  stripPrefill,
} from "~/features/git/prefill";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";

function RotateDialog({ connection, onClose }: { connection: GitConnection; onClose: () => void }) {
  const { t } = useTranslation();
  const rotate = useRotateGitConnection();
  const [token, setToken] = useState("");

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Rotate token")}
      description={t(
        "The old token is replaced, never kept. The new one is verified read-only first.",
      )}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!token.trim() || rotate.isPending}
            onClick={() =>
              rotate.mutate(
                { id: connection.id, token: token.trim() },
                {
                  onSuccess: () => {
                    onClose();
                    toast.success(t("Token rotated"));
                  },
                },
              )
            }
          >
            {rotate.isPending ? t("Saving…") : t("Rotate")}
          </Button>
        </>
      }
    >
      <Input
        type="password"
        label={t("New token")}
        autoComplete="off"
        fullWidth
        value={token}
        onChange={(event) => setToken(event.target.value)}
      />
    </Dialog>
  );
}

function ConnectionRow({
  connection,
  projectName,
  canManage,
}: {
  connection: GitConnection;
  projectName: string | undefined;
  canManage: boolean;
}) {
  const { t, i18n } = useTranslation();
  const test = useTestGitConnection();
  const ingest = useIngestGitConnection();
  const update = useUpdateGitConnection();
  const remove = useRemoveGitConnection();
  const [rotating, setRotating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const name = repositoryName(connection);

  const items: MenuItem[] = [
    {
      type: "route",
      title: t("Browse"),
      icon: GitBranch,
      to: `/repo/${connection.id}`,
    },
    {
      type: "button",
      title: t("Test connection"),
      icon: Stethoscope,
      onClick: () =>
        test.mutate(connection.id, {
          onSuccess: (probe) => {
            if (probe.ok) {
              toast.success(
                probe.latestMessage
                  ? t("Reachable — latest commit: {{message}}", { message: probe.latestMessage })
                  : t("Reachable"),
              );
            } else {
              toast.error(probe.error ?? t("Not reachable"));
            }
          },
        }),
    },
    ...(canManage
      ? [
          {
            type: "button" as const,
            title: t("Ingest history now"),
            icon: DownloadSimple,
            onClick: () =>
              ingest.mutate(
                { id: connection.id },
                {
                  onSuccess: (report) => {
                    if (report.available) {
                      toast.success(
                        t("{{seen}} commits seen, {{added}} added", {
                          seen: report.commitsSeen,
                          added: report.commitsAdded,
                        }),
                      );
                    } else {
                      toast.error(report.reason ?? t("Ingest failed"));
                    }
                  },
                },
              ),
          },
          {
            type: "button" as const,
            title: connection.enabled ? t("Disable") : t("Enable"),
            onClick: () => update.mutate({ id: connection.id, enabled: !connection.enabled }),
          },
          ...(connection.hasCredential
            ? [
                {
                  type: "button" as const,
                  title: t("Rotate token…"),
                  icon: ArrowsClockwise,
                  onClick: () => setRotating(true),
                },
              ]
            : []),
          { type: "separator" as const },
          {
            type: "button" as const,
            title: t("Remove…"),
            icon: Trash,
            dangerous: true,
            onClick: () => setConfirmRemove(true),
          },
        ]
      : []),
  ];

  const busy = test.isPending || ingest.isPending;

  return (
    <Row>
      <GitBranch size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">
          <Link
            to={"/repo/$connectionId" as never}
            params={{ connectionId: connection.id } as never}
            className="hover:underline"
          >
            {name}
          </Link>
          {connection.pathPrefix ? (
            <span className="text-text-tertiary"> / {connection.pathPrefix}</span>
          ) : null}
        </p>
        <p className="truncate text-small text-text-tertiary">
          {kindLabel(connection.kind, t)}
          {" · "}
          {connection.kind === "mount" ? connection.remoteUrl : connection.defaultBranch}
          {" · "}
          {projectName ?? t("Whole workspace")}
          {connection.lastError ? (
            <span className="text-danger">
              {" · "}
              {connection.lastError}
            </span>
          ) : null}
        </p>
      </div>
      <Pill tone={STATUS_TONE[connection.status]}>{statusLabel(connection.status, t)}</Pill>
      <span className="hidden w-24 text-small text-text-tertiary sm:inline">
        {connection.lastIngestedAt
          ? formatRelativeTime(connection.lastIngestedAt, i18n.language)
          : ""}
      </span>
      {busy ? (
        <Spinner size={16} label={t("Working…")} />
      ) : (
        <Menu items={items} trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />} />
      )}

      {rotating ? (
        <RotateDialog connection={connection} onClose={() => setRotating(false)} />
      ) : null}
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t("Remove repository?")}
        description={t(
          "{{name}} is disconnected and its credential, if any, deleted with it. Banked history stays.",
          { name },
        )}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={remove.isPending}
        onConfirm={() =>
          remove.mutate(connection.id, {
            onSuccess: () => {
              setConfirmRemove(false);
              toast.success(t("Repository removed"));
            },
          })
        }
      />
    </Row>
  );
}

export interface GitConnectionsPanelProps {
  /** Scope to one project; otherwise every connection in the workspace. */
  projectId?: string;
  canManage: boolean;
}

/**
 * The list and the verbs, shared by the settings section (every repository, with its scope)
 * and a project's own dialog (that project's only, connect button pre-scoped).
 */
export function GitConnectionsPanel({ projectId, canManage }: GitConnectionsPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useGitConnections(projectId ? { projectId } : {});
  const { data: projects } = useProjects();
  // On the way back from installing the app on GitHub the server appends
  // `githubInstall=<id>` (proven) or `githubInstall=unverified`: reopen the dialog on that
  // installation, or say plainly why it cannot be used. The marker is removed from the URL.
  const [returned] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("githubInstall");
    } catch {
      return null;
    }
  });
  // "Connect this repo to Docit" from VS Code (2026-09-28): `connect=…&branch=…&project=…&level=…`
  // opens the dialog prefilled. Only on the workspace-wide panel (a project's own dialog is
  // pre-scoped already). Read from the ROUTER's location, not window.location: the VS Code panel
  // runs slate on memory history, where window.location is the webview's own URL. The params
  // stay while the dialog is open (the GitHub install returns to this URL) and go when it closes.
  const router = useRouter();
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const [prefill] = useState<ConnectPrefill | null>(() =>
    projectId ? null : parseConnectPrefill(searchStr),
  );
  const [connecting, setConnecting] = useState(false);
  // canManage arrives with the workspace (async): open once it is known, never re-open after a close.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || !canManage) {
      return;
    }
    if (prefill || (returned !== null && returned !== "unverified")) {
      autoOpened.current = true;
      setConnecting(true);
    }
  }, [canManage, prefill, returned]);
  const closeDialog = () => {
    setConnecting(false);
    if (prefill) {
      const { pathname, searchStr: current, hash } = router.state.location;
      router.history.replace(`${pathname}${stripPrefill(current)}${hash ? `#${hash}` : ""}`);
    }
  };
  useEffect(() => {
    if (returned === null) {
      return;
    }
    if (returned === "unverified") {
      toast.error(
        t("GitHub did not confirm that installation is yours, so it cannot be used here."),
      );
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("githubInstall");
    window.history.replaceState(window.history.state, "", url.toString());
  }, [returned, t]);

  const projectName = (id: string | null) =>
    id ? projects?.find((project) => project.id === id)?.name : undefined;

  return (
    <div className="flex flex-col gap-4">
      {prefill && !canManage && !isLoading ? (
        <p role="status" className="text-small text-text-secondary">
          {t("Only a workspace admin can connect {{name}} here.", {
            name: parseRemote(prefill.remote)?.fullName ?? prefill.remote,
          })}
        </p>
      ) : null}
      <div className="flex items-center justify-end">
        {canManage ? (
          <Button variant="primary" icon={Plus} onClick={() => setConnecting(true)}>
            {t("Connect repository")}
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !data?.connections.length ? (
        <Empty icon={GitBranch} message={t("No repository connected yet")} />
      ) : (
        <Rows>
          {data.connections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              projectName={projectName(connection.projectId)}
              canManage={canManage}
            />
          ))}
        </Rows>
      )}

      {connecting ? (
        <GitConnectionDialog
          projectId={projectId ?? null}
          lockProject={Boolean(projectId)}
          secretsAvailable={data?.secretsAvailable ?? false}
          initialInstallationId={returned && returned !== "unverified" ? returned : null}
          prefill={prefill}
          onClose={closeDialog}
        />
      ) : null}
    </div>
  );
}
